import React, { useRef, useState, useEffect, useCallback } from 'react';
import { renderStrokes, floodFill } from '../drawingReplay';
import CircleTimer from './CircleTimer';
import UnresponsiveKickList from './UnresponsiveKickList';
import { audio } from '../audio';
import { saveDrawingDraft, loadDrawingDraft, clearDrawingDraft } from '../session';
import './DrawingCanvas.css';


const COLORS = [
  '#000000', '#666666', '#aaaaaa',
  '#ffffff', '#ff0013', '#990000',
  '#ff7829', '#964112', '#ffc126',
  '#b0701c', '#11b03c', '#017420',
  '#26c9ff', '#0050cd', '#ff008f',
  '#99004e', '#feafa8',
];
const DEFAULT_CUSTOM_COLOR = '#cb5a57';

const BRUSH_SIZES = [3, 6, 12, 20];

const TOOL_ICONS = { brush: 'draw', fill: 'format_color_fill', eraser: 'ink_eraser' };
const Icon = ({ name }) => <span className="material-symbols-outlined">{name}</span>;
const IconWithColor = ({ name, color, clipBar }) => (
  <span className="tool-icon-with-color">
    <span className={`material-symbols-outlined${clipBar ? ' clip-builtin-bar' : ''}`}>{name}</span>
    <span className="tool-color-bar" style={{ backgroundColor: color }} />
  </span>
);

const CANVAS_WIDTH = 1200;
const CANVAS_HEIGHT = 900;

function DrawingCanvas({ assignment, players, playerId, submittedCount, onSubmitDrawing, timeLeft: externalTimeLeft, currentRound, totalRounds, alreadySubmitted, isHost, onKickPlayer }) {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [color, setColor] = useState('#000000');
  const [customColor, setCustomColor] = useState(DEFAULT_CUSTOM_COLOR);
  const [brushSize, setBrushSize] = useState(6);
  const [tool, setTool] = useState('brush'); // brush, eraser, fill
  const [toolMenuOpen, setToolMenuOpen] = useState(false);
  const [sizeMenuOpen, setSizeMenuOpen] = useState(false);
  const [colorMenuOpen, setColorMenuOpen] = useState(false);
  const toolPickerRef = useRef(null);
  const sizePickerRef = useRef(null);
  const colorPickerRef = useRef(null);
  const [hasSubmitted, setHasSubmitted] = useState(alreadySubmitted || false);
  const noLimit = (externalTimeLeft === 0);
  const [timeLeft, setTimeLeft] = useState(noLimit ? 0 : (externalTimeLeft || 120));
  const maxTimeRef = useRef(noLimit ? 0 : (externalTimeLeft || 120));

  // Pinch-zoom state
  const [zoom, setZoom] = useState(1);
  const [panOffset, setPanOffset] = useState({ x: 0, y: 0 });
  const pinchRef = useRef(null); // { startDist, startZoom, startMid, startPan }

  // Portrait viewport panning state
  const [viewportPan, setViewportPan] = useState(0.5); // 0=left, 0.5=center, 1=right
  const [isPortraitMode, setIsPortraitMode] = useState(
    typeof window !== 'undefined' && window.matchMedia('(max-width: 600px)').matches
  );
  const [containerDims, setContainerDims] = useState({ width: 0, height: 0 });
  const canvasFitRef = useRef(null);
  const [canvasFitDims, setCanvasFitDims] = useState({ width: 0, height: 0 });

  // Stroke recording
  const drawingStartTime = useRef(Date.now());
  const [strokes, setStrokes] = useState([]);           // committed strokes
  const currentStroke = useRef(null);                    // in-progress stroke
  const lastPos = useRef(null);

  // Refs to hold latest draw/stopDrawing for window-level listeners
  const drawRef = useRef(null);
  const stopDrawingRef = useRef(null);
  // Guard for the re-render effect: only re-render on edit, not on initial mount
  const editingAfterSubmit = useRef(false);

  // Initialize canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return; // not rendered when already submitted
    const ctx = canvas.getContext('2d');
    canvas.width = CANVAS_WIDTH;
    canvas.height = CANVAS_HEIGHT;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    drawingStartTime.current = Date.now();

    // Restore draft strokes if they belong to this assignment
    const savedStrokes = loadDrawingDraft(assignment?.text);
    if (savedStrokes) {
      setStrokes(savedStrokes);
      renderStrokes(ctx, savedStrokes);
    }

    // Prevent iOS Safari from scrolling/bouncing while on drawing screen
    // Everything fits in viewport via flex layout, so no scrolling needed
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.width = '100%';
    document.body.style.height = '100%';

    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.height = '';
    };
  }, [assignment?.text]);

  // Re-render existing strokes when the user clicks Edit after submitting.
  // The editingAfterSubmit guard prevents this from running at initial mount
  // (which would clear the canvas before effect 1 has a chance to restore the draft).
  useEffect(() => {
    if (hasSubmitted) { editingAfterSubmit.current = true; return; }
    if (!editingAfterSubmit.current) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    canvas.width = CANVAS_WIDTH;
    canvas.height = CANVAS_HEIGHT;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    renderStrokes(ctx, strokes);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasSubmitted]);

  // Persist strokes so a refresh or reopened tab restores the drawing
  useEffect(() => {
    if (strokes.length === 0) return;
    saveDrawingDraft(assignment?.text, strokes);
  }, [strokes, assignment?.text]);

  // Timer countdown — just counts, no submission logic (skip if no-limit)
  useEffect(() => {
    if (noLimit) return;
    const interval = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) { clearInterval(interval); return 0; }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-submit when the timer reaches 0 (skip if no-limit).
  // Runs as a normal effect so `strokes` is the latest render snapshot —
  // no stale-closure risk. Also captures any mid-stroke via currentStroke.current.
  // Rasterize the current canvas to a PNG ArrayBuffer for server-side
  // persistence (gallery + completed_games). Returns null if unavailable;
  // server stores null and Home renders a placeholder.
  const captureCanvasPng = () => new Promise((resolve) => {
    const canvas = canvasRef.current;
    if (!canvas || !canvas.toBlob) {
      resolve(null);
      return;
    }
    canvas.toBlob((blob) => {
      if (!blob) { resolve(null); return; }
      blob.arrayBuffer().then(resolve, () => resolve(null));
    }, 'image/png');
  });

  useEffect(() => {
    if (noLimit) return;
    if (timeLeft !== 0) return;
    audio.stopDrawStroke();
    clearDrawingDraft();
    const totalDuration = Date.now() - drawingStartTime.current;
    const inProgress = currentStroke.current;
    const allStrokes =
      inProgress && inProgress.points?.length > 0
        ? [...strokes, { ...inProgress, endTime: totalDuration }]
        : strokes;
    if (allStrokes.length === 0) return; // nothing drawn — server would reject anyway
    captureCanvasPng().then((pngBuf) => {
      onSubmitDrawing({
        canvas: { width: CANVAS_WIDTH, height: CANVAS_HEIGHT, background: '#ffffff' },
        strokes: allStrokes,
        totalDuration,
      }, pngBuf);
    });
    setHasSubmitted(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeLeft]);

  // Timer tick sounds for last 10 seconds
  useEffect(() => {
    if (noLimit || hasSubmitted) return;
    if (timeLeft === 0) {
      audio.timerEnd();
    } else if (timeLeft <= 10) {
      audio.timerTick(timeLeft);
    }
  }, [timeLeft]); // eslint-disable-line react-hooks/exhaustive-deps


  // Close portrait pickers on outside click
  useEffect(() => {
    const handleOutside = (e) => {
      if (toolPickerRef.current && !toolPickerRef.current.contains(e.target)) {
        setToolMenuOpen(false);
      }
      if (sizePickerRef.current && !sizePickerRef.current.contains(e.target)) {
        setSizeMenuOpen(false);
      }
      if (colorPickerRef.current && !colorPickerRef.current.contains(e.target)) {
        setColorMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutside);
    document.addEventListener('touchstart', handleOutside);
    return () => {
      document.removeEventListener('mousedown', handleOutside);
      document.removeEventListener('touchstart', handleOutside);
    };
  }, []);

  // Detect portrait mode via media query
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 600px)');
    const handler = (e) => setIsPortraitMode(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  // Track container dimensions for viewport pan calculations
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const observer = new ResizeObserver(entries => {
      const { width, height } = entries[0].contentRect;
      setContainerDims({ width, height });
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Track canvas-fit-container for landscape contain-fit sizing
  useEffect(() => {
    const el = canvasFitRef.current;
    if (!el) return;
    const observer = new ResizeObserver(entries => {
      const { width, height } = entries[0].contentRect;
      setCanvasFitDims({ width, height });
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const getElapsedMs = () => Date.now() - drawingStartTime.current;

  const getCanvasCoords = (e) => {
    const canvas = canvasRef.current;
    const container = containerRef.current;

    let clientX, clientY;
    if (e.touches) {
      clientX = e.touches[0].clientX;
      clientY = e.touches[0].clientY;
    } else {
      clientX = e.clientX;
      clientY = e.clientY;
    }

    // In portrait viewport mode, use the canvas bounding rect directly.
    // It already accounts for translateX (viewport pan) + any zoom transforms.
    if (isPortraitMode) {
      const canvasRect = canvas.getBoundingClientRect();
      return {
        x: (clientX - canvasRect.left) / canvasRect.width * CANVAS_WIDTH,
        y: (clientY - canvasRect.top) / canvasRect.height * CANVAS_HEIGHT,
      };
    }

    // Landscape mode — invert zoom/pan transform manually
    const containerRect = container.getBoundingClientRect();
    const containerW = containerRect.width;
    const containerH = containerRect.height;
    const relX = clientX - containerRect.left - containerW / 2;
    const relY = clientY - containerRect.top - containerH / 2;
    const unzoomedX = (relX - panOffset.x) / zoom + containerW / 2;
    const unzoomedY = (relY - panOffset.y) / zoom + containerH / 2;

    return {
      x: unzoomedX * (CANVAS_WIDTH / containerW),
      y: unzoomedY * (CANVAS_HEIGHT / containerH),
    };
  };

  const renderToCanvas = useCallback((strokeList) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    renderStrokes(ctx, strokeList);
  }, []);

  // ── Pinch-zoom handlers ──
  const getTouchDist = (t1, t2) => Math.hypot(t2.clientX - t1.clientX, t2.clientY - t1.clientY);
  const getTouchMid = (t1, t2) => ({
    x: (t1.clientX + t2.clientX) / 2,
    y: (t1.clientY + t2.clientY) / 2,
  });

  const handlePinchStart = (e) => {
    if (e.touches.length < 2) return;
    // Cancel any in-progress drawing stroke
    if (isDrawing && currentStroke.current) {
      audio.stopDrawStroke();
      currentStroke.current = null;
      setIsDrawing(false);
      lastPos.current = null;
    }
    const t1 = e.touches[0], t2 = e.touches[1];
    pinchRef.current = {
      startDist: getTouchDist(t1, t2),
      startZoom: zoom,
      startMid: getTouchMid(t1, t2),
      startPan: { ...panOffset },
    };
  };

  const handlePinchMove = (e) => {
    if (!pinchRef.current || e.touches.length < 2) return;
    e.preventDefault();
    const t1 = e.touches[0], t2 = e.touches[1];
    const dist = getTouchDist(t1, t2);
    const mid = getTouchMid(t1, t2);
    const newZoom = Math.min(5, Math.max(1, pinchRef.current.startZoom * (dist / pinchRef.current.startDist)));
    const dx = mid.x - pinchRef.current.startMid.x;
    const dy = mid.y - pinchRef.current.startMid.y;
    setZoom(newZoom);
    setPanOffset({
      x: pinchRef.current.startPan.x + dx,
      y: pinchRef.current.startPan.y + dy,
    });
  };

  const handlePinchEnd = (e) => {
    if (pinchRef.current) {
      pinchRef.current = null;
      // If zoom snapped back to ~1, reset fully
      if (zoom <= 1.05) {
        setZoom(1);
        setPanOffset({ x: 0, y: 0 });
      }
    }
  };

  const resetZoom = () => {
    setZoom(1);
    setPanOffset({ x: 0, y: 0 });
  };

  const startDrawing = (e) => {
    // Don't start drawing during pinch
    if (e.touches && e.touches.length >= 2) return;
    if (pinchRef.current) return;
    e.preventDefault();
    const coords = getCanvasCoords(e);
    const t = getElapsedMs();

    // Fill bucket — instant operation, no drag needed
    if (tool === 'fill') {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      floodFill(ctx, coords.x, coords.y, color);
      audio.fillPop();
      const fillOp = {
        id: crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`,
        type: 'fill',
        x: coords.x,
        y: coords.y,
        color,
        startTime: t,
        endTime: t,
      };
      setStrokes(prev => [...prev, fillOp]);
      return;
    }

    audio.startDrawStroke(tool);

    // Start a new stroke operation
    currentStroke.current = {
      id: crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`,
      type: 'stroke',
      tool,
      color,
      size: brushSize,
      points: [{ x: coords.x, y: coords.y, t }],
      startTime: t,
      endTime: t,
    };

    // Draw the initial dot on-screen immediately
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const effectiveColor = tool === 'eraser' ? '#ffffff' : color;
    const effectiveSize = tool === 'eraser' ? brushSize * 2 : brushSize;
    ctx.beginPath();
    ctx.arc(coords.x, coords.y, effectiveSize / 2, 0, Math.PI * 2);
    ctx.fillStyle = effectiveColor;
    ctx.fill();

    lastPos.current = coords;
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing || !currentStroke.current) return;
    if (pinchRef.current) return; // pinch in progress
    e.preventDefault();
    const coords = getCanvasCoords(e);
    const t = getElapsedMs();

    const isInBounds =
      coords.x >= 0 && coords.x <= CANVAS_WIDTH &&
      coords.y >= 0 && coords.y <= CANVAS_HEIGHT;

    if (!isInBounds) {
      // Outside canvas — just update lastPos so re-entry resumes smoothly
      lastPos.current = coords;
      return;
    }

    // Record point
    currentStroke.current.points.push({ x: coords.x, y: coords.y, t });
    currentStroke.current.endTime = t;

    // Update drawing sound velocity
    const pts = currentStroke.current.points;
    if (pts.length >= 2) {
      const prev = pts[pts.length - 2];
      const curr = pts[pts.length - 1];
      const dx = curr.x - prev.x;
      const dy = curr.y - prev.y;
      const dt = Math.max(1, curr.t - prev.t);
      audio.updateDrawVelocity(Math.sqrt(dx * dx + dy * dy) / dt * 1000);
    }

    // Draw segment on-screen immediately
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const effectiveColor = currentStroke.current.tool === 'eraser' ? '#ffffff' : currentStroke.current.color;
    const effectiveSize = currentStroke.current.tool === 'eraser' ? currentStroke.current.size * 2 : currentStroke.current.size;

    // If lastPos was out of bounds, just move to current point (no connecting line from outside)
    const lastInBounds = lastPos.current &&
      lastPos.current.x >= 0 && lastPos.current.x <= CANVAS_WIDTH &&
      lastPos.current.y >= 0 && lastPos.current.y <= CANVAS_HEIGHT;

    ctx.beginPath();
    if (lastInBounds) {
      ctx.moveTo(lastPos.current.x, lastPos.current.y);
      ctx.lineTo(coords.x, coords.y);
      ctx.strokeStyle = effectiveColor;
      ctx.lineWidth = effectiveSize;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.stroke();
    } else {
      // Re-entering canvas — draw a dot at entry point
      ctx.arc(coords.x, coords.y, effectiveSize / 2, 0, Math.PI * 2);
      ctx.fillStyle = effectiveColor;
      ctx.fill();
    }

    lastPos.current = coords;
  };
  drawRef.current = draw;

  const stopDrawing = () => {
    if (isDrawing && currentStroke.current) {
      audio.stopDrawStroke();
      // Capture the stroke before clearing the ref, since setStrokes
      // updater runs asynchronously and would see null otherwise
      const finishedStroke = currentStroke.current;
      currentStroke.current = null;
      setStrokes(prev => [...prev, finishedStroke]);
      setIsDrawing(false);
      lastPos.current = null;
    }
  };
  stopDrawingRef.current = stopDrawing;

  // Attach window-level mousemove/mouseup so drawing continues outside the canvas
  useEffect(() => {
    if (!isDrawing) return;

    const handleWindowMouseMove = (e) => {
      if (drawRef.current) drawRef.current(e);
    };
    const handleWindowMouseUp = () => {
      if (stopDrawingRef.current) stopDrawingRef.current();
    };

    window.addEventListener('mousemove', handleWindowMouseMove);
    window.addEventListener('mouseup', handleWindowMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleWindowMouseMove);
      window.removeEventListener('mouseup', handleWindowMouseUp);
    };
  }, [isDrawing]);

  const handleUndo = () => {
    setStrokes(prev => {
      if (prev.length === 0) return prev;
      const newStrokes = prev.slice(0, -1);
      renderToCanvas(newStrokes);
      return newStrokes;
    });
  };

  // True when the canvas has at least one visible stroke (i.e. a non-clear stroke
  // exists after the last clear operation, or any stroke if there's no clear).
  const hasVisibleDrawing = strokes.length > 0 && strokes[strokes.length - 1].type !== 'clear';

  const handleSubmit = async () => {
    if (!hasVisibleDrawing) return;
    clearDrawingDraft();

    // Build the drawing data model
    const drawingData = {
      canvas: {
        width: CANVAS_WIDTH,
        height: CANVAS_HEIGHT,
        background: '#ffffff',
      },
      strokes: strokes,
      totalDuration: getElapsedMs(),
    };

    console.log('[DrawingCanvas] Submitting drawing:', {
      strokeCount: drawingData.strokes.length,
      totalDuration: drawingData.totalDuration,
      firstStroke: drawingData.strokes[0] ? {
        id: drawingData.strokes[0].id,
        type: drawingData.strokes[0].type,
        pointCount: drawingData.strokes[0].points?.length,
        startTime: drawingData.strokes[0].startTime,
        endTime: drawingData.strokes[0].endTime,
      } : null,
    });

    const pngBuf = await captureCanvasPng();
    onSubmitDrawing(drawingData, pngBuf);
    audio.submitChime();
    setHasSubmitted(true);
  };

  const handleSetTool = (t) => { setTool(t); audio.toolSwitch(); };
  const handleSetColor = (c) => { setColor(c); if (tool === 'eraser') setTool('brush'); audio.colorSwitch(); };

  // Landscape: contain-fit 4:3 canvas within available space (minus sidebars)
  const landscapeWrapperStyle = (() => {
    if (isPortraitMode) return undefined;
    if (canvasFitDims.width === 0 || canvasFitDims.height === 0) return undefined;
    // Measure sidebar widths from the DOM so we don't hardcode
    const fitEl = canvasFitRef.current;
    let sidebarW = 0;
    if (fitEl) {
      for (const child of fitEl.children) {
        if (child !== containerRef.current) {
          sidebarW += child.offsetWidth;
        }
      }
    }
    const gap = 8; // matches CSS gap
    const gaps = sidebarW > 0 ? gap * 2 : 0; // gap on each side of canvas
    const ratio = CANVAS_WIDTH / CANVAS_HEIGHT; // 4/3
    const availW = canvasFitDims.width - sidebarW - gaps;
    const availH = canvasFitDims.height;
    let w, h;
    if (availW / availH > ratio) {
      h = availH;
      w = h * ratio;
    } else {
      w = availW;
      h = w / ratio;
    }
    return { width: w, height: h, maxHeight: 'none', aspectRatio: 'unset' };
  })();

  // Compute canvas transform (viewport pan + zoom)
  const canvasTransformStyle = (() => {
    if (isPortraitMode && containerDims.height > 0) {
      const canvasDisplayW = containerDims.height * (CANVAS_WIDTH / CANVAS_HEIGHT);
      const maxTx = Math.max(0, canvasDisplayW - containerDims.width);
      const vpTx = -viewportPan * maxTx;
      if (zoom > 1) {
        return {
          transform: `translateX(${vpTx}px) scale(${zoom}) translate(${panOffset.x / zoom}px, ${panOffset.y / zoom}px)`,
          transformOrigin: 'center center',
        };
      }
      return { transform: `translateX(${vpTx}px)` };
    }
    if (zoom > 1) {
      return {
        transform: `scale(${zoom}) translate(${panOffset.x / zoom}px, ${panOffset.y / zoom}px)`,
        transformOrigin: 'center center',
      };
    }
    return undefined;
  })();

  // Viewport indicator dimensions (for portrait slider)
  const vpCanvasDisplayW = containerDims.height > 0 ? containerDims.height * (CANVAS_WIDTH / CANVAS_HEIGHT) : 1;
  const vpViewportFraction = Math.min(1, containerDims.width / vpCanvasDisplayW);
  const vpPanRange = 1 - vpViewportFraction;
  const needsViewportPan = isPortraitMode && vpPanRange > 0.01;

  const waitingPlayers = players.filter(p => !p.submitted);
  const waitingText =
    waitingPlayers.length === 0 ? 'Almost done!' :
    waitingPlayers.length === 1 ? `Waiting for ${waitingPlayers[0].name}...` :
    waitingPlayers.length === 2 ? `Waiting for ${waitingPlayers[0].name} and ${waitingPlayers[1].name}...` :
    `Waiting for ${waitingPlayers.length} more...`;

  return (
    <div className="drawing-container">
      <div className="drawing-card">
        {/* Header — outside scroll so it's always visible */}
        <div className="drawing-header">
          <div className="drawing-prompt-display">
            <span className="drawing-prompt-label">whispered prompt</span>
            <span className="drawing-prompt-text">"{assignment?.text}"</span>
            {totalRounds > 0 && (
              <span className="drawing-round-info">Round {(currentRound || 0) + 1} of {totalRounds}</span>
            )}
          </div>
          <div className="drawing-header-right">
            <CircleTimer timeLeft={timeLeft} maxTime={maxTimeRef.current} />
            {hasSubmitted && (noLimit || timeLeft > 0) ? (
              <button className="edit-drawing-button-top" onClick={() => setHasSubmitted(false)}>Edit</button>
            ) : hasSubmitted ? (
              <span className="drawing-submitted-badge"><span className="material-symbols-outlined" style={{fontSize:'12px'}}>check_circle</span> Submitted</span>
            ) : null}
          </div>
        </div>

        <div className="drawing-card-scroll">
          <div className="canvas-area">
            <div className="canvas-fit-container" ref={canvasFitRef}>
              <div className="left-sidebar" style={hasSubmitted ? {visibility: 'hidden'} : undefined}>
                <div className="tool-sidebar">
                  <button className={`sidebar-btn ${tool === 'brush' ? 'active' : ''}`} onClick={() => handleSetTool('brush')} title="Brush"><IconWithColor name={TOOL_ICONS.brush} color={color} /></button>
                  <button className={`sidebar-btn ${tool === 'fill' ? 'active' : ''}`} onClick={() => handleSetTool('fill')} title="Fill"><IconWithColor name={TOOL_ICONS.fill} color={color} clipBar /></button>
                  <button className={`sidebar-btn ${tool === 'eraser' ? 'active' : ''}`} onClick={() => handleSetTool('eraser')} title="Eraser"><Icon name={TOOL_ICONS.eraser} /></button>
                  <div />
                  <button className="sidebar-btn" onClick={handleUndo} disabled={strokes.length === 0} title="Undo"><Icon name="undo" /></button>
                </div>
                <div className="color-sidebar">
                  <div className="color-grid">
                    {COLORS.map(c => (
                      <button
                        key={c}
                        className={`color-swatch ${color === c && tool !== 'eraser' ? 'active' : ''}`}
                        style={{ backgroundColor: c }}
                        onClick={() => handleSetColor(c)}
                        title={c}
                      />
                    ))}
                    <label
                      className={`color-swatch eyedropper-swatch ${!COLORS.includes(color) && tool !== 'eraser' ? 'active' : ''}`}
                      style={{ backgroundColor: customColor }}
                      title="Custom color"
                    >
                      <span className="material-symbols-outlined eyedropper-icon">colorize</span>
                      <input
                        type="color"
                        className="color-input-hidden"
                        value={customColor}
                        onChange={e => { setCustomColor(e.target.value); handleSetColor(e.target.value); }}
                      />
                    </label>
                  </div>
                </div>
              </div>
              <div
                className="canvas-wrapper"
                ref={containerRef}
                style={landscapeWrapperStyle}
                onTouchStart={!hasSubmitted ? (e) => { if (e.touches.length >= 2) handlePinchStart(e); } : undefined}
                onTouchMove={!hasSubmitted ? (e) => { if (pinchRef.current && e.touches.length >= 2) handlePinchMove(e); } : undefined}
                onTouchEnd={!hasSubmitted ? handlePinchEnd : undefined}
              >
                <canvas
                  ref={canvasRef}
                  className={`drawing-canvas${hasSubmitted ? ' drawing-canvas--submitted' : ''}`}
                  style={canvasTransformStyle}
                  onMouseDown={!hasSubmitted ? startDrawing : undefined}
                  onTouchStart={!hasSubmitted ? startDrawing : undefined}
                  onTouchMove={!hasSubmitted ? draw : undefined}
                  onTouchEnd={!hasSubmitted ? stopDrawing : undefined}
                />
                {zoom > 1 && (
                  <button className="zoom-reset-btn" onClick={resetZoom} title="Reset zoom">
                    <span className="material-symbols-outlined" style={{ fontSize: 16 }}>zoom_out_map</span>
                  </button>
                )}
              </div>
            </div>
          </div>

          {needsViewportPan && (
            <div className="viewport-slider-container">
              <div className="viewport-slider-track-area">
                <div className="viewport-indicator-track">
                  <div
                    className="viewport-indicator-window"
                    style={{
                      width: `${vpViewportFraction * 100}%`,
                      left: `${viewportPan * vpPanRange * 100}%`,
                    }}
                  />
                </div>
                <input
                  type="range"
                  className="viewport-slider"
                  min="0"
                  max="1"
                  step="0.001"
                  value={viewportPan}
                  onChange={(e) => setViewportPan(parseFloat(e.target.value))}
                />
              </div>
            </div>
          )}

          {!hasSubmitted && (
            <div className="size-bar">
              {BRUSH_SIZES.map(size => (
                <button
                  key={size}
                  className={`size-bar-btn ${brushSize === size ? 'active' : ''}`}
                  onClick={() => setBrushSize(size)}
                  title={`${size}px`}
                >
                  <span className="size-dot" style={{ width: size, height: size }} />
                </button>
              ))}
              <button className="submit-drawing-button size-bar-submit" onClick={handleSubmit} disabled={!hasVisibleDrawing}>
                Submit <Icon name="check" />
              </button>
            </div>
          )}

          {!hasSubmitted && (
            <div className="portrait-bar">
              <div className="portrait-controls">
                <div className="picker-wrapper" ref={toolPickerRef}>
                  <button
                    className="portrait-picker-btn"
                    onClick={() => { setToolMenuOpen(o => !o); setSizeMenuOpen(false); setColorMenuOpen(false); }}
                    title={tool}
                  >
                    {(tool === 'brush' || tool === 'fill')
                      ? <IconWithColor name={TOOL_ICONS[tool]} color={color} clipBar={tool === 'fill'} />
                      : <Icon name={TOOL_ICONS[tool]} />}
                    <span className="picker-arrow">▴</span>
                  </button>
                  {toolMenuOpen && (
                    <div className="portrait-picker-menu">
                      {['brush', 'fill', 'eraser'].map(t => (
                        <button
                          key={t}
                          className={`picker-option ${tool === t ? 'active' : ''}`}
                          onClick={() => { handleSetTool(t); setToolMenuOpen(false); }}
                          title={t.charAt(0).toUpperCase() + t.slice(1)}
                        >
                          {(t === 'brush' || t === 'fill')
                            ? <IconWithColor name={TOOL_ICONS[t]} color={color} clipBar={t === 'fill'} />
                            : <Icon name={TOOL_ICONS[t]} />}
                        </button>
                      ))}
                      <button className="picker-option" disabled title="Coming soon">?</button>
                    </div>
                  )}
                </div>
                <div className="picker-wrapper" ref={sizePickerRef}>
                  <button
                    className="portrait-picker-btn"
                    onClick={() => { setSizeMenuOpen(o => !o); setToolMenuOpen(false); setColorMenuOpen(false); }}
                    title={`Size: ${brushSize}px`}
                  >
                    <span className="size-dot portrait-picker-dot" style={{ width: brushSize, height: brushSize }} />
                    <span className="picker-arrow">▴</span>
                  </button>
                  {sizeMenuOpen && (
                    <div className="portrait-picker-menu">
                      {BRUSH_SIZES.map(size => (
                        <button
                          key={size}
                          className={`picker-option ${brushSize === size ? 'active' : ''}`}
                          onClick={() => { setBrushSize(size); setSizeMenuOpen(false); }}
                          title={`${size}px`}
                        >
                          <span className="size-dot" style={{ width: size, height: size }} />
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <div className="portrait-undo-redo">
                <button className="portrait-picker-btn" onClick={handleUndo} disabled={strokes.length === 0} title="Undo"><Icon name="undo" /></button>
              </div>
              <div className="picker-wrapper" ref={colorPickerRef}>
                <button
                  className="portrait-picker-btn portrait-color-btn"
                  onClick={() => { setColorMenuOpen(o => !o); setToolMenuOpen(false); setSizeMenuOpen(false); }}
                  title="Color"
                >
                  <span className="portrait-color-swatch" style={{ backgroundColor: color }} />
                  <span className="picker-arrow">▴</span>
                </button>
                {colorMenuOpen && (
                  <div className="portrait-picker-menu portrait-color-menu">
                    {COLORS.map(c => (
                      <button
                        key={c}
                        className={`picker-option color-picker-option ${color === c && tool !== 'eraser' ? 'active' : ''}`}
                        style={{ backgroundColor: c }}
                        onClick={() => { handleSetColor(c); setColorMenuOpen(false); }}
                        title={c}
                      />
                    ))}
                    <label
                      className={`picker-option color-picker-option custom-color-option ${!COLORS.includes(color) && tool !== 'eraser' ? 'active' : ''}`}
                      style={{ backgroundColor: customColor }}
                      title="Custom color"
                    >
                      <span className="material-symbols-outlined eyedropper-icon">colorize</span>
                      <input
                        type="color"
                        className="color-input-hidden"
                        value={customColor}
                        onChange={e => { setCustomColor(e.target.value); handleSetColor(e.target.value); setColorMenuOpen(false); }}
                      />
                    </label>
                  </div>
                )}
              </div>
              <button className="submit-drawing-button portrait-submit" onClick={handleSubmit} disabled={!hasVisibleDrawing}>
                <Icon name="check" />
              </button>
            </div>
          )}

          {hasSubmitted && (
            <div className="drawing-submitted-bar">
              <span className="material-symbols-outlined" style={{fontSize:'13px'}}>check_circle</span>
              <span className="drawing-submitted-status"> Submitted</span>
              <span className="drawing-submitted-waiting"> · {waitingText}</span>
            </div>
          )}
          <UnresponsiveKickList players={players} isHost={isHost} onKickPlayer={onKickPlayer} />
        </div>
      </div>
    </div>
  );
}

export default DrawingCanvas;
